import React from 'react';
import PropTypes from 'prop-types';
import styles from "./Button.module.scss";

/**
 * Button component - A customizable button using Bootstrap classes and CSS variables for contrast colors
 * 
 * @param {Object} props - Component props
 * @param {string} props.variant - Button style variant (primary, secondary, link, transparent, danger)
 * @param {boolean} props.outline - Whether to use the outline variant of the button
 * @param {string} props.size - Button size (sm, md, lg)
 * @param {string} props.letterCase - Text transformation (uppercase, lowercase, capitalize)
 * @param {function} props.onClick - Click handler
 * @param {boolean} props.disabled - Whether the button is disabled
 * @param {string} props.type - Button type (button, submit, reset)
 * @param {string} props.className - Additional CSS classes
 */
const Button = ({
	variant = 'primary',
	outline = false,
	letterCase,
	size = 'md',
	className = '',
	children,
	style = {},
	...props
}) => {
	const letterCaseClass = letterCase ? `text-${letterCase}` : '';
	const bootstrapVariantClass = outline 
		? {
			primary: 'btn-outline-primary',
			secondary: 'btn-outline-secondary',
			light: 'btn-outline-light',
			dark: 'btn-outline-dark',
			link: 'btn-link',
			transparent: 'btn-transparent',
			danger: 'btn-outline-danger'
		}[variant] || 'btn-outline-primary'
		: {
			primary: 'btn-primary',
			secondary: 'btn-secondary',
			light: 'btn-light',
			dark: 'btn-dark',
			link: 'btn-link',
			transparent: 'btn-transparent',
			danger: 'btn-danger'
		}[variant] || 'btn-primary';

	const bootstrapClass = `btn ${bootstrapVariantClass} btn-${size} ${letterCaseClass}`;
	const variantClass = variant ? 
		outline ? 
			styles[`Button--${variant}-outline`] : 
			styles[`Button--${variant}`] 
		: '';

	const contrastColor = outline 
		? {
			primary: 'var(--bs-primary)',
			secondary: 'var(--bs-secondary)',
			danger: 'var(--bs-danger)',
			link: 'var(--bs-primary)',
			transparent: 'inherit'
		}[variant] || 'var(--bs-primary)'
		: {
			primary: 'var(--bs-primary-contrast)',
			secondary: 'var(--bs-secondary-contrast)',
			danger: '#fff',
			link: 'var(--bs-primary)',
			transparent: 'inherit'
		}[variant] || '#fff';

	return (
		<button
			className={`${styles.Button} ${variantClass} ${bootstrapClass} ${className}`}
			style={{ color: contrastColor, ...style }}
			{...props}>
			{children}
		</button>
	);
};

Button.propTypes = {
	variant: PropTypes.oneOf(['primary', 'secondary', 'link', 'transparent', 'danger', 'light']),
	outline: PropTypes.bool,
	size: PropTypes.string,
	letterCase: PropTypes.string,
	onClick: PropTypes.func,
	disabled: PropTypes.bool,
	type: PropTypes.string,
	className: PropTypes.string,
	style: PropTypes.object,
	children: PropTypes.node
};

Button.defaultProps = {
	variant: 'primary',
	outline: false,
	size: 'md',
	type: 'button'
};

export default Button;